import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import MultiSelect from '../../components/MultiSelect';

const ThingsToDo = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [thingsToDo, setThingsToDo] = useState([]);
  const [locationName, setLocationName] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listing');
  const [seoData, setSeoData] = useState({
    thingsToDoMetaTitle: '',
    thingsToDoMetaKeyword: '',
    thingsToDoMetaDescription: '',
  });
  const [keywordLinks, setKeywordLinks] = useState([{ keyword: '', link: '' }]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [savingSeo, setSavingSeo] = useState(false);

  useEffect(() => {
    if (!locationId) return;
    fetchThingsToDo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const fetchThingsToDo = async () => {
    try {
      setLoading(true);
      const response = await apiService.post('thingsToDoList', { locationId });
      setThingsToDo(response.data.data || []);
      if (response.data.locationName) {
        setLocationName(response.data.locationName);
      }
      setSeoData({
        thingsToDoMetaTitle: response.data.thingsToDoMetaTitle || '',
        thingsToDoMetaKeyword: response.data.thingsToDoMetaKeyword || '',
        thingsToDoMetaDescription: response.data.thingsToDoMetaDescription || '',
      });
      const kw = response.data.thingsToDoKeywordLink && response.data.thingsToDoKeywordLink.length > 0
        ? response.data.thingsToDoKeywordLink.map(k => ({ keyword: k.keyword || '', link: k.link || '' }))
        : [{ keyword: '', link: '' }];
      setKeywordLinks(kw);
      const selPkgs = (response.data.thingsToDoPackages || []).map(p => typeof p === 'object' ? p._id : p);
      setSelectedPackages(selPkgs);
      const allPkgs = (response.data.packages || []).map(p => ({
        ...p,
        name: p.vendorId?.business_name ? `${p.name} (${p.vendorId.business_name})` : p.name
      }));
      setAllPackages(allPkgs);
    } catch (error) {
      toast.error('Failed to load things to do');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addKeywordLink = () => setKeywordLinks([...keywordLinks, { keyword: '', link: '' }]);
  const removeKeywordLink = (index) => setKeywordLinks(keywordLinks.filter((_, i) => i !== index));
  const updateKeywordLink = (index, field, value) => {
    const updated = [...keywordLinks];
    updated[index][field] = value;
    setKeywordLinks(updated);
  };

  const handleSaveSeo = async (e) => {
    if (e) e.preventDefault();
    try {
      setSavingSeo(true);
      const filteredKeywordLinks = keywordLinks.filter(k => k.keyword?.trim() || k.link?.trim());
      const deleteAllKeywordLink = filteredKeywordLinks.length === 0 ? '1' : '0';

      const response = await apiService.post('updateThingsToDoSEO', {
        locationId,
        ...seoData,
        thingsToDoKeywordLink: filteredKeywordLinks,
        delete_all_keywordLink: deleteAllKeywordLink,
        thingsToDoPackages: selectedPackages
      });
      if (response.status) {
        toast.success('SEO points and keyword links updated successfully');
        fetchThingsToDo();
      } else {
        toast.error(response.message || 'Failed to update');
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error('Save error:', error);
    } finally {
      setSavingSeo(false);
    }
  };

  const handleAddNew = () => {
    navigate(`/things-to-do/${locationId}/add`);
  };

  const handleEdit = (id) => {
    navigate(`/things-to-do/${locationId}/edit/${id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      await apiService.get(`deleteThingsToDo/${id}`);
      toast.success('Deleted successfully');
      fetchThingsToDo();
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error('Delete error:', error);
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      await apiService.get(`thingsToDoStatus/${id}`);
      setThingsToDo(thingsToDo.map(item => item._id === id ? { ...item, status: item.status === 1 ? 0 : 1 } : item));
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error('Status error:', error);
    }
  };

  return (
    <div className="p-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <nav className="text-xs sm:text-sm flex flex-wrap items-center">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 no-underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/location" className="text-gray-500 hover:text-gray-700 no-underline">Locations</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Things To Do</span>
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-3 sm:p-4 border-b flex justify-between items-center bg-white rounded-t-lg">
          {locationName && (
            <h2 className="text-lg font-semibold text-gray-900">Things To Do - {locationName}</h2>
          )}
          {activeTab === 'listing' && (
            <button
              onClick={handleAddNew}
              className="px-2 sm:px-4 py-2 text-white rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-95"
              style={{ backgroundColor: '#009ef7' }}
            >
              <i className="bi bi-plus-lg"></i>
              Add new
            </button>
          )}
        </div>

        <div className="flex border-b border-gray-200 overflow-x-auto bg-gray-50/50">
          <button
            type="button"
            onClick={() => setActiveTab('listing')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 font-semibold text-sm transition-all whitespace-nowrap ${
              activeTab === 'listing'
                ? 'border-[#009ef7] text-[#009ef7] bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="bi bi-list-task"></i>
            Listing
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('seo')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 font-semibold text-sm transition-all whitespace-nowrap ${
              activeTab === 'seo'
                ? 'border-[#009ef7] text-[#009ef7] bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="bi bi-search"></i>
            SEO Points
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('keywordLink')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 font-semibold text-sm transition-all whitespace-nowrap ${
              activeTab === 'keywordLink'
                ? 'border-[#009ef7] text-[#009ef7] bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="bi bi-link-45deg"></i>
            Keyword Links
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('packages')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 font-semibold text-sm transition-all whitespace-nowrap ${
              activeTab === 'packages'
                ? 'border-[#009ef7] text-[#009ef7] bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="bi bi-box-seam"></i>
            Packages
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === 'listing' && (
            <>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
                  <span className="ml-2 text-gray-600">Loading...</span>
                </div>
              ) : thingsToDo.length === 0 ? (
                <div className="text-center py-12">
                  <i className="bi bi-list-task text-4xl text-gray-300 mb-2"></i>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Things To Do Found</h3>
                  <p className="text-gray-500">Add your first item to get started.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-gray-500 text-xs uppercase">
                        <th className="px-2 sm:px-4 py-3">#</th>
                        <th className="px-2 sm:px-4 py-3">Title</th>
                        <th className="px-2 sm:px-4 py-3">Description</th>
                        <th className="px-2 sm:px-4 py-3">Duration</th>
                        <th className="px-2 sm:px-4 py-3">Address</th>
                        <th className="px-2 sm:px-4 py-3">Status</th>
                        <th className="px-2 sm:px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {thingsToDo.map((item, index) => (
                        <tr key={item._id} className="hover:bg-gray-50">
                          <td className="px-2 sm:px-4 py-3 text-sm">{index + 1}</td>
                          <td className="px-2 sm:px-4 py-3 text-sm">{item.title}</td>
                          <td className="px-2 sm:px-4 py-3 text-sm max-w-xs truncate">{item.description}</td>
                          <td className="px-2 sm:px-4 py-3 text-sm">{item.duration || '--'}</td>
                          <td className="px-2 sm:px-4 py-3 text-sm">{item.address || '--'}</td>
                          <td className="px-2 sm:px-4 py-3">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={item.status === 1}
                                onChange={() => handleStatusToggle(item._id)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" style={item.status === 1 ? { backgroundColor: '#009ef7' } : {}}></div>
                            </label>
                          </td>
                          <td className="px-2 sm:px-4 py-3 text-center">
                            <button
                              onClick={() => handleEdit(item._id)}
                              className="text-green-600 hover:text-green-800 mr-3"
                            >
                              <i className="bi bi-pencil text-lg"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(item._id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <i className="bi bi-trash text-lg"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {activeTab === 'seo' && (
            <form onSubmit={handleSaveSeo} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Title</label>
                <input
                  type="text"
                  value={seoData.thingsToDoMetaTitle}
                  onChange={(e) => setSeoData({ ...seoData, thingsToDoMetaTitle: e.target.value })}
                  placeholder="Enter meta title"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Keywords</label>
                <textarea
                  value={seoData.thingsToDoMetaKeyword}
                  onChange={(e) => setSeoData({ ...seoData, thingsToDoMetaKeyword: e.target.value })}
                  placeholder="Enter meta keywords"
                  rows="3"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Description</label>
                <textarea
                  value={seoData.thingsToDoMetaDescription}
                  onChange={(e) => setSeoData({ ...seoData, thingsToDoMetaDescription: e.target.value })}
                  placeholder="Enter meta description"
                  rows="3"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                />
              </div>
              <div className="flex justify-end pt-4 border-t mt-4">
                <button
                  type="submit"
                  disabled={savingSeo}
                  className="px-6 py-2.5 text-white rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-95"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  {savingSeo ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></span>
                      Saving...
                    </>
                  ) : (
                    'Save SEO'
                  )}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'keywordLink' && (
            <form onSubmit={handleSaveSeo} className="space-y-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-semibold text-gray-700">Manage Keyword Links</h4>
                <button
                  type="button"
                  onClick={addKeywordLink}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  <i className="bi bi-plus-circle"></i> Add Row
                </button>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Keyword</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Target Link / URL</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-16">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {keywordLinks.length > 0 ? (
                      keywordLinks.map((item, index) => (
                        <tr key={index} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={item.keyword}
                              onChange={(e) => updateKeywordLink(index, 'keyword', e.target.value)}
                              placeholder="e.g. Travel Guide"
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={item.link}
                              onChange={(e) => updateKeywordLink(index, 'link', e.target.value)}
                              placeholder="e.g. https://example.com/guide"
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => removeKeywordLink(index)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="px-4 py-6 text-center text-gray-400 text-sm">
                          No keyword links added yet. Click "Add Row" to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end pt-4 border-t mt-4">
                <button
                  type="submit"
                  disabled={savingSeo}
                  className="px-6 py-2.5 text-white rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-95"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  {savingSeo ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Keyword Links'
                  )}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'packages' && (
            <form onSubmit={handleSaveSeo} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Select Packages for Things To Do</label>
                <MultiSelect
                  options={allPackages}
                  value={selectedPackages}
                  onChange={(value) => setSelectedPackages(value)}
                  placeholder="Select packages"
                />
              </div>

              <div className="flex justify-end pt-4 border-t mt-4">
                <button
                  type="submit"
                  disabled={savingSeo}
                  className="px-6 py-2.5 text-white rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-95"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  {savingSeo ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Packages'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThingsToDo;
